import * as React from 'react';
import FormControl from '@mui/material/FormControl';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Box, Step, StepContent, StepLabel, Stepper } from '@mui/material';
import { DashboardChartT } from 'business/definitions';
import { Alert } from 'lib/alert/models';
import { useGetAlert } from 'lib/alert/hooks/useGetAlert';
import { useEffect, useState } from 'react';
import { convertBackendFilterOperatorToFrontFilterOperator } from 'helpers/filterHelper';
import AlertTypeStep from './AlertTypeStep';
import { AlertFormContext } from '../Contexts/AlertFormContext';
import AlertThresholdStep from './AlertThresholdStep';
import AlertVentilation from './AlertVentilation';
import AlertPerimeters from './AlertPerimeters';
import AlertPersonalization from './AlertPersonalization';
import AlertSharings from './AlertSharings';
import { getCompletedStepLabel } from './helpers';
import Loading from '../../Loading';
import { useModalContext } from '../Contexts/ModalContext';
import AlertFormProvider from 'components/ChartAlert/Contexts/AlertFormProvider';
import AlertSentenceProvider from 'components/ChartAlert/Contexts/AlertSentenceProvider';

const StyDrawerContentText = styled(Typography, {
    name: 'StyDrawerContentText',
})({
    fontWeight: 400,
    color: '#666',
    fontSize: '1rem',
    lineHeight: '1.5rem',
    padding: '1rem 0',
});

const StyTitle = styled(Typography, {
    name: 'StyTitle',
})({
    display: 'inline',
});

type Props = {
    chart: DashboardChartT;
    chartTitle: string;
    alertId: number;
};

const AlertEditForm = ({ chart, chartTitle, alertId }: Props) => {
    const { setDirty } = useModalContext();
    const [defaultValues, setDefaultValues] = useState<Partial<Alert>>({});

    const AlertSteps: ReadonlyArray<JSX.Element> = [
        <AlertTypeStep key="step-type" />,
        <AlertThresholdStep chart={chart} key="threshold-step" />,
        <AlertVentilation key="ventilation-step" />,
        <AlertPerimeters chart={chart} key="perimeters-step" />,
        <AlertPersonalization key="personalization-step" />,
        <AlertSharings chartKey={chart.key} key="sharings-step" />,
    ];

    const { t } = useTranslation();

    const { data, isLoading, isFetching } = useGetAlert(alertId);

    const getOptionalLabel = (
        alert: Partial<Alert>,
        activeStep: number,
        step: number,
    ): string | null => {
        if (activeStep < step) {
            return null;
        }
        if (activeStep === step) {
            return t(`alert.create.steps.${step}.description`);
        }
        return getCompletedStepLabel(step, alert, t);
    };

    useEffect(() => {
        if (!isLoading && !isFetching && data) {
            setDefaultValues({
                name: data.name,
                emailSubject: data.emailSubject,
                emailContent: data.emailContent,
                type: data.type,
                recipientsEmails: data.recipients.map((r) => r.email),

                condition: {
                    threshold: data.condition.threshold,
                    operation: data.condition.operation,
                },

                metric: {
                    field: data.metric.field,
                    aggregation: data.metric.aggregation,
                },

                filters: data.filters?.map((filter) =>
                    convertBackendFilterOperatorToFrontFilterOperator({
                        field: filter.field,
                        value: filter.value,
                        valueScale: filter.valueScale,
                        operation: filter.operation,
                        displayIndex: filter.displayIndex,
                        type: filter.type,
                    }),
                ),

                dimension: data.dimension,
            });
        }
    }, [data, isLoading, isFetching]);

    return (
        <>
            <StyTitle variant="h5">{chartTitle}</StyTitle>
            <FormControl sx={{ position: 'relative', height: '100%' }}>
                <StyDrawerContentText>
                    {t('alert.create.description')}
                </StyDrawerContentText>
                {Object.keys(defaultValues).length > 0 ? (
                    <AlertFormProvider
                        setHasChanges={setDirty}
                        defaultValues={defaultValues}
                        defaultActiveStep={1}
                    >
                        <AlertSentenceProvider>
                            <AlertFormContext.Consumer>
                                {({ alert, activeStep }) => (
                                    <Stepper
                                        activeStep={activeStep}
                                        orientation="vertical"
                                    >
                                        {AlertSteps.map((AlertStep, index) => (
                                            <Step
                                                key={AlertStep.key}
                                                active={index > 0}
                                            >
                                                <StepLabel
                                                    optional={getOptionalLabel(
                                                        alert,
                                                        activeStep,
                                                        index,
                                                    )}
                                                >
                                                    {t(
                                                        `alert.create.steps.${index}.title`,
                                                    )}
                                                </StepLabel>
                                                <StepContent>
                                                    {AlertStep}
                                                </StepContent>
                                            </Step>
                                        ))}
                                    </Stepper>
                                )}
                            </AlertFormContext.Consumer>
                        </AlertSentenceProvider>
                    </AlertFormProvider>
                ) : (
                    <Box sx={{ mt: 3 }}>
                        <Loading />
                    </Box>
                )}
            </FormControl>
        </>
    );
};

export default AlertEditForm;
